import { createApi } from '@reduxjs/toolkit/query/react';
import { BenefitType, ReferentPriceType, USState } from '@strata/constants';
import { capitalizeString } from '@strata/helper-functions';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';

import { TREATMENT_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ReferralDetails {
    referralId: string;
    treatmentId: string;
    segmentId: string;
    payerId: string;
    productServiceOid: string;
    isBrand: boolean;
    diagnosisOid: string;
    diagnosisCode?: string;
    friendlyId: string;
    memberId?: string;
    memberDOB: string;
    memberFirstName?: string;
    memberLastName?: string;
    memberCity?: string;
    memberState?: USState;
    memberZip?: string;
    priceIsFrozen?: boolean;
    priceSource?: string;
    winningBid?: number;
    referentPriceType?: ReferentPriceType;
    referralExpiration?: string;
    submittedAt: string;
    referralStart?: string;
    outreachDate?: string;
    status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    qty?: number;
    refills?: number;
    strength?: string;
    placeOfService?: string;
    prescriberNpi?: string;
    practiceNpi?: string;
    supplierNpi?: string;
    benefit: BenefitType;
    auths: { caseId: string; authStart: string; authExpiration: string }[];
    copayAssistanceMessage?: {
        title: string;
        shortMessage: string;
        longMessage?: string;
    };
}

export const treatmentApi = createApi({
    baseQuery: getBaseQuery(TREATMENT_API_URL, true),
    refetchOnMountOrArgChange: true,
    reducerPath: 'treatmentApi',
    endpoints: (build) => ({
        getReferralDetails: build.query<ReferralDetails, { payerId: string; referralId: string }>({
            query: ({ payerId, referralId }) => `${payerId}/referrals/${referralId}`,
            transformResponse: (response: {
                referral_id: string;
                treatment_id: string;
                segment_id: string;
                payer_id: string;
                product_service_oid: string;
                is_brand: boolean;
                diagnosis_oid: string;
                diagnosis_code?: string;
                friendly_id: string;
                place_of_service?: string;
                benefit_type: BenefitType;
                referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
                created_at: string;
                outreach_date?: string;
                auths: { auth_id: string; auth_start: string; auth_expiration: string }[];
                member: {
                    member_id?: string;
                    first_name?: string;
                    last_name?: string;
                    dob: string;
                    member_city?: string;
                    member_state?: USState;
                    member_zip?: string;
                };
                referral_period?: { start?: string; expiration: string };
                servicing_info?: {
                    servicing_parent_pharmacy_npi: string;
                    servicing_rate: {
                        rate?: string;
                        referent_price_type: ReferentPriceType;
                        price_is_frozen: boolean;
                        price_source: string;
                    };
                };
                prescription_info?: {
                    prescriber_npi?: string;
                    practice_npi?: string;
                    product_qty?: string;
                    rx_refillcount?: number;
                    strength?: string;
                };
                copay_assistance_message?: {
                    title: string;
                    short_message: string;
                    long_message?: string;
                };
            }) => ({
                referralId: response.referral_id,
                treatmentId: response.treatment_id,
                auths: response.auths?.map((auth) => ({
                    caseId: auth.auth_id,
                    authStart: auth.auth_start,
                    authExpiration: auth.auth_expiration,
                })),
                benefit: response.benefit_type?.trim() as BenefitType,
                diagnosisCode: response.diagnosis_code,
                diagnosisOid: response.diagnosis_oid,
                friendlyId: response.friendly_id,
                isBrand: response.is_brand,
                memberId: response.member?.member_id,
                memberDOB: response.member?.dob,
                memberFirstName: capitalizeString(response.member?.first_name),
                memberLastName: capitalizeString(response.member?.last_name),
                memberCity: response.member?.member_city,
                memberState: response.member?.member_state,
                memberZip: response.member?.member_zip,
                payerId: response.payer_id,
                placeOfService: response.place_of_service?.trim(),
                practiceNpi: response.prescription_info?.practice_npi,
                prescriberNpi: response.prescription_info?.prescriber_npi,
                priceIsFrozen: response.servicing_info?.servicing_rate?.price_is_frozen,
                priceSource: response.servicing_info?.servicing_rate?.price_source,
                productServiceOid: response.product_service_oid,
                qty: response.prescription_info?.product_qty
                    ? Number(response.prescription_info.product_qty)
                    : undefined,
                referentPriceType: response.servicing_info?.servicing_rate?.referent_price_type,
                referralExpiration: response.referral_period?.expiration,
                referralStart: response.referral_period?.start,
                outreachDate: response.outreach_date,
                refills: response.prescription_info?.rx_refillcount,
                segmentId: response.segment_id,
                status: response.referral_status?.trim() as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses],
                strength: response.prescription_info?.strength,
                submittedAt: response.created_at,
                supplierNpi: response.servicing_info?.servicing_parent_pharmacy_npi,
                winningBid: response.servicing_info?.servicing_rate?.rate
                    ? Number(response.servicing_info?.servicing_rate?.rate)
                    : undefined,
                copayAssistanceMessage: response.copay_assistance_message
                    ? {
                          title: response.copay_assistance_message.title,
                          shortMessage: response.copay_assistance_message.short_message,
                          longMessage: response.copay_assistance_message.long_message,
                      }
                    : undefined,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving referral details.',
            ),
        }),
    }),
});

export const { useGetReferralDetailsQuery } = treatmentApi;
